import type { ClosureReason, OpenDetail, PoolSection, PoolStatus, SplashPad } from "../../shared/types";
import { GEO_DENIED_COPY, GEO_LABEL_IDLE, GEO_LABEL_LOCATING, GEO_LABEL_LOCATED, SPLASH_CLOSURE_REASONS, SPLASH_OPEN_DETAILS, addressHtml, esc, favToggleHtml, footerHtml, geoButtonHtml, openDetailPickerHtml, paintSectionStatus, reasonPickerHtml, renderTimeline, sectionStatusView, statusLineHtml } from "../util";
import { type Filters, type SummaryCounts, matchesQuery, parseFilters, poolIsOpenNow } from "../filters";
import { viewTabs } from "./list";
import { searchBoxHtml, skeletonListHtml, wireSearchBox } from "./chrome";
import { navigate } from "../router";
import { CITY } from "../city";
import { cachedStatus, loadAllStatuses, loadPools } from "../store";
import { getRecentReports } from "../api";
import { enrichClosedReport, reportSection } from "../report";
import { getFavorites, toggleFavorite } from "../favorites";
import { trackAction } from "../datadog";
import {
  type LatLng,
  cachedLocation,
  getUserLocation,
  hasLocationOptIn,
  haversineMeters,
  lastLocationError,
  milesAwayLabel,
} from "../geo";

// The catalog the splash view renders, resolved per city: DC DPR spray pads
// (grouped by ward) or Arlington spraygrounds (grouped by neighborhood). The
// `splash-<id>` poolId convention is shared by both.
const SPLASH_PADS = CITY.splashCatalog;

/** The reportable poolId for a splash pad (mirrors `splash-${id}` in the seed). */
function splashPoolId(pad: SplashPad): string {
  return `splash-${pad.id}`;
}

/** A pad's distance from `loc`, or Infinity when it has no usable coordinates. */
function padDistanceMeters(pad: SplashPad, loc: LatLng): number {
  if (!Number.isFinite(pad.lat) || !Number.isFinite(pad.lng)) return Infinity;
  return haversineMeters({ lat: pad.lat, lng: pad.lng }, loc);
}

/**
 * Order pads for display: favorites always pin to the top, then nearest-first
 * when a location fix exists, else the default ward-then-name order.
 */
function sortPads(pads: SplashPad[], loc: LatLng | null, favs: Set<string> = new Set()): SplashPad[] {
  const favFirst = (a: SplashPad, b: SplashPad): number =>
    (favs.has(splashPoolId(b)) ? 1 : 0) - (favs.has(splashPoolId(a)) ? 1 : 0);
  if (loc) {
    return [...pads].sort(
      (a, b) =>
        favFirst(a, b) ||
        padDistanceMeters(a, loc) - padDistanceMeters(b, loc) ||
        a.name.localeCompare(b.name),
    );
  }
  return [...pads].sort((a, b) => favFirst(a, b) || a.ward - b.ward || a.name.localeCompare(b.name));
}

/** The nearest locatable pad to `loc`, for the "Closest: …" banner. */
function nearestPad(pads: SplashPad[], loc: LatLng): { pad: SplashPad; meters: number } | null {
  let best: { pad: SplashPad; meters: number } | null = null;
  for (const pad of pads) {
    const meters = padDistanceMeters(pad, loc);
    if (!Number.isFinite(meters)) continue;
    if (!best || meters < best.meters) best = { pad, meters };
  }
  return best;
}

/**
 * "0.4 mi away" line for a pad card, shown only when a location fix exists and
 * the pad has usable coordinates. Pure string helper, re-render-safe.
 */
function padDistanceLine(pad: SplashPad, loc: LatLng | null): string {
  if (!loc) return "";
  const meters = padDistanceMeters(pad, loc);
  if (!Number.isFinite(meters)) return "";
  return `<p class="card-distance">${milesAwayLabel(meters)}</p>`;
}

/**
 * One splash-pad card: name, address + ward, the optional weekly maintenance
 * window, a live status badge, and a Mark Open/Closed control reported on the
 * pad's `main` section. The closed flow reuses the shared reason picker (splash
 * reasons only; no reopen-ETA row — the pads are unstaffed).
 *
 * The name links to the pad's detail page (`#/pool/splash-<id>`), exactly like a
 * pool card: the detail pages already existed and were reachable from the map,
 * but nothing on the Splash tab pointed at them.
 */
function padCard(pad: SplashPad, loc: LatLng | null = null): string {
  const poolId = splashPoolId(pad);
  // DC pads carry an off-window (`maintenanceWindow`); Arlington spraygrounds
  // carry a full weekly-hours summary (`scheduleNote`). Render whichever applies.
  const note = pad.maintenanceWindow
    ? `Maintenance: ${esc(pad.maintenanceWindow)}`
    : pad.scheduleNote
      ? `Hours: ${esc(pad.scheduleNote)}`
      : "";
  const maint = note ? `<p class="splash-maint">${note}</p>` : "";
  // DC groups pads by ward; Arlington (no wards) shows the neighborhood instead.
  const locale = CITY.showWard
    ? ` · Ward ${pad.ward}`
    : pad.neighborhood
      ? ` · ${esc(pad.neighborhood)}`
      : "";
  return `
    <article class="pool-card splash-card" data-pool-card="${esc(poolId)}">
      <div class="card-head">
        <h2><a class="pool-link" href="#/pool/${encodeURIComponent(poolId)}">${esc(pad.name)}</a></h2>
        ${favToggleHtml(poolId)}
      </div>
      <p class="addr">${addressHtml(pad.address)}${locale}</p>
      ${padDistanceLine(pad, loc)}
      ${maint}
      <div class="section" data-pool="${esc(poolId)}" data-section="main">
        <div class="section-head">
          <span class="section-label">Splash pad</span>
        </div>
        ${statusLineHtml()}
        <p class="hero" data-role="freshness"></p>
        <div class="report-buttons">
          <button class="btn btn-open" data-action="report" data-status="open">It's open</button>
          <button class="btn btn-closed" data-action="report" data-status="closed">It's closed</button>
        </div>
        ${reasonPickerHtml(SPLASH_CLOSURE_REASONS)}
        ${openDetailPickerHtml(SPLASH_OPEN_DETAILS)}
        <details class="card-history">
          <summary>Recent reports</summary>
          <ol class="timeline" data-role="timeline"><li class="muted">Loading…</li></ol>
        </details>
      </div>
    </article>`;
}

/**
 * The tab's own filter hash. Splash carries ONE filter — "Open now" — since
 * indoor/sloped are pool properties a pad can't have. Kept local (rather than
 * bent into `filtersToHash`, which serializes the pool tabs) so the shared
 * `viewTabs` links keep working unchanged.
 */
export function splashHash(filters: Filters): string {
  return filters.openNow ? "#/splash?open=1" : "#/splash";
}

/** The "Open now" toggle row, mirroring the pool list's filter bar. */
function filterBar(filters: Filters): string {
  return `
    <section class="filters" aria-label="Filters">
      <label class="filter-toggle">
        <input type="checkbox" data-filter="open"${filters.openNow ? " checked" : ""} />
        Open now
      </label>
    </section>`;
}

/**
 * Open / total tallies across the pad catalog. Mirrors `summarizeOpen` for
 * pools (same `SummaryCounts` shape, same "focus section" idea) but reads the
 * pad catalog directly: a pad has exactly ONE reportable section (`main`), so
 * `poolIsOpenNow` — the shared filter predicate the Open-now checkbox uses — is
 * already the focus-section answer. Sharing that predicate is what keeps the
 * count line and the filter from ever disagreeing.
 */
function summarizeOpenPads(
  pads: SplashPad[],
  statusOf: (poolId: string) => PoolStatus | undefined,
): SummaryCounts {
  let open = 0;
  for (const pad of pads) {
    if (poolIsOpenNow(statusOf(splashPoolId(pad)))) open += 1;
  }
  return { open, total: pads.length };
}

/** Lower-case plural noun for this city's pads ("splash pads" / "spraygrounds"). */
const PAD_NOUN = CITY.splashTabLabel.toLowerCase();

/**
 * "12 of 35 splash pads open now" — and, when a filter is hiding pads,
 * "· showing 12". The denominator is always the FULL catalog so it never
 * shifts as filters change (same convention as the pool list's count line).
 */
function summaryHtml(counts: SummaryCounts, showing: number): string {
  const tail = showing === counts.total ? "" : ` · showing ${showing}`;
  return `<p class="summary-count">${counts.open} of ${counts.total} ${PAD_NOUN} open now${tail}</p>`;
}

/**
 * Empty-list message, with a one-tap way back to the full list. Mirrors the
 * pool list's empty state (0c123585 + d8b45a64): whichever control renders
 * carries `data-action="clear-search"`, so tapping it always empties the search
 * box too — one tap restores the full list no matter whether the Open-now
 * filter, the search, or both emptied it.
 */
function emptyPadsHtml(filters: Filters, search: string): string {
  const searching = search.trim().length > 0;
  const msg = searching
    ? `No ${PAD_NOUN} match your search.`
    : `No ${PAD_NOUN} are open right now.`;
  if (filters.openNow) {
    return `<div class="empty"><p>${msg}</p><a class="clear-filters" href="#/splash" data-action="clear-search">Show all ${PAD_NOUN}</a></div>`;
  }
  if (searching) {
    return `<div class="empty"><p>${msg}</p><button type="button" class="clear-filters" data-action="clear-search">Clear search</button></div>`;
  }
  return `<p class="empty">No ${PAD_NOUN} to show.</p>`;
}

/**
 * The sorted card list (or the empty state when a filter/search has hidden
 * every pad). Used both for the first paint and every re-render, so the two can
 * never disagree.
 */
function padListHtml(
  pads: SplashPad[],
  loc: LatLng | null,
  filters: Filters,
  search: string,
): string {
  const sorted = sortPads(pads, loc, getFavorites());
  if (!sorted.length) return emptyPadsHtml(filters, search);
  return sorted.map((p) => padCard(p, loc)).join("");
}

/** Paint a pad's main-section status tile from a PoolStatus (Direction-1 confidence-led). */
function applyStatus(root: HTMLElement, poolId: string, status: PoolStatus): void {
  const main = status.sections.find((s) => s.section === "main");
  if (!main) return;
  const section = root.querySelector<HTMLElement>(
    `.section[data-pool="${CSS.escape(poolId)}"][data-section="main"]`,
  );
  if (!section) return;
  paintSectionStatus(section, sectionStatusView(main));
}

/**
 * Everything one paint of the pad list needs. Bundled (like the pool list's
 * `ListRender`) because the list is re-rendered from several places: initial
 * data, favorite toggle, location fix, and each debounced search keystroke.
 */
interface PadRender {
  app: HTMLElement;
  /** What actually renders: the Open-now filter AND the search already applied. */
  pads: SplashPad[];
  live: boolean;
  loc: LatLng | null;
  filters: Filters;
  /** Current search query, for the search-aware empty state. */
  search: string;
  /** Reset the search box (wired onto the empty state's clear control). */
  onClearSearch: () => void;
}

/**
 * Render the pad cards into `.pool-list`, then (re)bind handlers and paint
 * statuses. Takes the raw (unsorted) pad array and sorts internally so that
 * a favorite toggle can re-render by calling this function again with the same
 * array — safe to re-run (rebuilds innerHTML before binding fresh handlers).
 *
 * The first call also replaces the skeleton cards the chrome painted before the
 * catalog landed (18c5d959).
 */
function renderPadList(ctx: PadRender): void {
  const { app, pads, live, loc, filters, search } = ctx;
  const listEl = app.querySelector<HTMLElement>(".pool-list");
  if (!listEl) return;
  listEl.innerHTML = padListHtml(pads, loc, filters, search);

  // Repaint the count line from whatever statuses are cached right now — free to
  // call again as the batch lands (it re-reads the cache, never re-fetches).
  const updateSummary = (): void => {
    const summaryEl = app.querySelector<HTMLElement>('[data-role="summary"]');
    if (summaryEl) summaryEl.innerHTML = summaryHtml(summarizeOpenPads(SPLASH_PADS, cachedStatus), pads.length);
  };
  updateSummary();

  // Favorite toggles: flip persisted state, then re-render so the pad moves to/from
  // the top of the list. Mirrors the list view's favorite handler.
  listEl.querySelectorAll<HTMLButtonElement>('[data-action="favorite"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const card = btn.closest<HTMLElement>("[data-pool-card]");
      const poolId = card?.dataset.poolCard;
      if (!poolId) return;
      const fav = toggleFavorite(poolId);
      trackAction("favorite_toggle", { poolId, nowFavorited: fav });
      renderPadList(ctx);
    });
  });

  // Empty-state escape hatch: the "Show all" link / "Clear search" button also
  // empties the search box (the link additionally navigates, which re-renders
  // the view from scratch).
  listEl.querySelectorAll<HTMLElement>('[data-action="clear-search"]').forEach((el) => {
    el.addEventListener("click", () => ctx.onClearSearch());
  });

  // Report buttons: "It's open" submits immediately; "It's closed" reveals
  // the reason picker first (submit happens via the reopen row), preserving the
  // one-tap path via Skip/Skip.
  listEl.querySelectorAll<HTMLButtonElement>('[data-action="report"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const section = btn.closest<HTMLElement>(".section")!;
      const poolId = section.dataset.pool!;
      const sectionName = section.dataset.section as PoolSection;
      const status = btn.dataset.status as "open" | "closed";
      const badge = section.querySelector<HTMLElement>('[data-role="status"]')!;
      const picker = section.querySelector<HTMLElement>('[data-role="reason-picker"]');
      if (status === "closed" && picker) {
        // Commit the closed status on the FIRST tap (durable even if the user
        // abandons the follow-up), THEN reveal the reason picker. Stash the
        // written row's ts so the reopen chip enriches that exact row.
        void reportSection(poolId, sectionName, "closed", badge, (s) => applyStatus(app, poolId, s)).then(
          (ts) => {
            if (ts === undefined) return;
            section.dataset.reportTs = String(ts);
            picker.hidden = false;
          },
        );
        return;
      }
      if (picker) picker.hidden = true;
      void reportSection(poolId, sectionName, status, badge, (s) => applyStatus(app, poolId, s));
      // After the plain open report fires, reveal the optional detail add-on.
      if (status === "open") {
        const detailPicker = section.querySelector<HTMLElement>('[data-role="open-detail-picker"]');
        if (detailPicker) detailPicker.hidden = false;
      }
    });
  });

  // Open-detail chips: send a second (rate-limit-exempt) open report with the
  // chosen detail, then collapse the picker. "Skip" just collapses it.
  listEl.querySelectorAll<HTMLButtonElement>('[data-role="open-detail-picker"] .chip').forEach((chip) => {
    chip.addEventListener("click", () => {
      const section = chip.closest<HTMLElement>(".section")!;
      const poolId = section.dataset.pool!;
      const sectionName = section.dataset.section as PoolSection;
      const badge = section.querySelector<HTMLElement>('[data-role="status"]')!;
      const detailPicker = section.querySelector<HTMLElement>('[data-role="open-detail-picker"]');
      const detail = (chip.dataset.detail || undefined) as OpenDetail | undefined;
      if (detailPicker) detailPicker.hidden = true;
      if (!detail) return;
      void reportSection(poolId, sectionName, "open", badge, (s) => applyStatus(app, poolId, s), undefined, undefined, detail);
    });
  });

  // Reason chips: splash pads are UNSTAFFED, so we never ask when the pad will
  // reopen — nobody at the pad can answer that, and a guess printed next to a
  // crowd status reads as fact. The chosen reason therefore enriches the
  // already-committed closed report immediately (no new report, no rollup) and
  // the picker collapses. "Skip" (empty data-reason) sends nothing; the plain
  // closed report already landed on the first tap.
  listEl.querySelectorAll<HTMLButtonElement>('[data-role="reason-picker"] .chip').forEach((chip) => {
    chip.addEventListener("click", () => {
      const section = chip.closest<HTMLElement>(".section")!;
      const poolId = section.dataset.pool!;
      const sectionName = section.dataset.section as PoolSection;
      const reasonPicker = section.querySelector<HTMLElement>('[data-role="reason-picker"]');
      const reason = (chip.dataset.reason || undefined) as ClosureReason | undefined;
      if (reasonPicker) reasonPicker.hidden = true;
      const targetTs = Number(section.dataset.reportTs);
      if (reason && Number.isFinite(targetTs)) {
        void enrichClosedReport(poolId, sectionName, targetTs, (s) => applyStatus(app, poolId, s), reason);
      }
    });
  });

  // Expandable per-card history: collapsed by default, fetched lazily the first
  // time a card is opened (guarded by data-loaded). Mirrors the pools list — a
  // splash pad reports on its `main` section, so the timeline reads that section.
  listEl.querySelectorAll<HTMLDetailsElement>(".card-history").forEach((details) => {
    details.addEventListener("toggle", () => {
      if (!details.open || details.dataset.loaded) return;
      const section = details.closest<HTMLElement>(".section");
      const list = details.querySelector<HTMLElement>('[data-role="timeline"]');
      if (!section || !list) return;
      details.dataset.loaded = "1";
      const poolId = section.dataset.pool!;
      if (!live) {
        list.innerHTML = '<li class="muted">History unavailable offline.</li>';
        return;
      }
      getRecentReports(poolId, "main")
        .then((res) => renderTimeline(list, res.reports))
        .catch(() => {
          list.innerHTML = '<li class="muted">History unavailable offline.</li>';
        });
    });
  });

  // Paint cached statuses immediately, then warm the rest with one batch call.
  for (const pad of pads) {
    const cached = cachedStatus(splashPoolId(pad));
    if (cached) applyStatus(app, splashPoolId(pad), cached);
  }
  if (live) {
    loadAllStatuses().then(() => {
      for (const pad of pads) {
        const status = cachedStatus(splashPoolId(pad));
        if (status) applyStatus(app, splashPoolId(pad), status);
      }
      updateSummary();
    });
  }
}

/**
 * The static chrome for the Splash tab: everything that depends only on `CITY`
 * and the parsed filters, i.e. everything paintable BEFORE the catalog request
 * resolves (18c5d959). `.pool-list` is seeded with skeleton cards and both the
 * summary and `data-role="dev-banner"` are left empty — those are the only
 * pieces that need the fetch to have landed.
 */
function splashChromeHtml(filters: Filters): string {
  return `
    <header class="topbar">
      <div class="topbar-head">
        <h1>${CITY.displayName}</h1>
        <p class="header-contact">${geoButtonHtml()}<a href="#/about">Contact</a><a href="${CITY.donateUrl}" target="_blank" rel="noopener">Donate</a></p>
      </div>
      <p class="tagline">${CITY.splashTagline}</p>
      <p class="geo-banner" data-role="geo-banner" hidden></p>
      <div data-role="dev-banner"></div>
      ${viewTabs("splash", filters)}
    </header>
    <p class="view-intro">
      ${CITY.splashIntro()}
    </p>
    ${searchBoxHtml(`Search ${PAD_NOUN} by name`)}
    ${filterBar(filters)}
    <main>
      <div class="summary" data-role="summary"></div>
      <div class="pool-list">${skeletonListHtml()}</div>
      ${CITY.splashSeasonNote ? `<p class="splash-note">${esc(CITY.splashSeasonNote)}</p>` : ""}
    </main>
    ${footerHtml("splash")}`;
}

/** The offline/sample-data notice — only knowable once `loadPools` has answered. */
const DEV_BANNER_HTML = '<p class="dev-banner">Showing sample data. Backend not connected yet.</p>';

/**
 * Splash Pads tab: DC DPR spray parks as crowd-reportable cards. Each pad reports
 * on its `main` section (open/closed), warmed via the same batch status call the
 * List/Map use. Sorted by ward then name (or nearest-first once the user opts into
 * location), with the season note + per-pad maintenance windows preserved.
 *
 * Rendered in two phases (18c5d959): the chrome + skeleton cards paint
 * synchronously — this is Arlington's HOME view, so a blank screen here is the
 * whole site — and the cards, count line and offline banner fill in when the
 * catalog lands.
 */
export async function renderSplash(
  app: HTMLElement,
  query: URLSearchParams = new URLSearchParams(),
): Promise<void> {
  // Kick the status batch off alongside the catalog so the round-trips overlap
  // (the store dedups, so the repaint below shares this same in-flight call).
  const statusP = loadAllStatuses();
  // The Splash tab now carries the shared "Open now" filter in its own hash
  // (`#/splash?open=1`); the other tab links keep serializing through viewTabs.
  const filters = parseFilters(query);

  // --- Phase 1: instant chrome (nothing awaited yet) -----------------------
  app.innerHTML = splashChromeHtml(filters);

  // View state phase 2 fills in; the handlers below close over these bindings
  // so a filter toggled (or a search typed) before the data lands still works.
  let live = true;
  let ready = false;
  let search = "";
  /** Open-now filter applied, search NOT yet applied. */
  let matching: SplashPad[] = [];
  // Location is opt-in (never auto-prompted); seed from any session fix.
  let loc = cachedLocation();

  /** What should be on screen right now: the filter AND the live search query. */
  const visible = (): SplashPad[] => matching.filter((p) => matchesQuery(p.name, search));

  const paint = (): void => {
    // No-op until the catalog has landed — the skeletons stay up meanwhile.
    if (!ready) return;
    renderPadList({
      app,
      pads: visible(),
      live,
      loc,
      filters,
      search,
      onClearSearch: () => searchBox.clear(),
    });
  };

  // Filter-as-you-type. Debounced, and deliberately NOT routed through the URL.
  const searchBox = wireSearchBox(app, (q) => {
    search = q;
    paint();
  });

  // The Open-now toggle just changes the hash; the router re-renders this view.
  app.querySelector<HTMLInputElement>('[data-filter="open"]')?.addEventListener("change", (e) => {
    navigate(splashHash({ ...filters, openNow: (e.target as HTMLInputElement).checked }));
  });

  // --- Location opt-in: "Use my location" (mirrors the List tab) ----------
  const geoBtn = app.querySelector<HTMLButtonElement>('[data-action="locate"]');
  const geoBanner = app.querySelector<HTMLElement>('[data-role="geo-banner"]');

  const paintBanner = (): void => {
    if (!geoBanner) return;
    if (!loc) {
      geoBanner.hidden = true;
      geoBanner.textContent = "";
      return;
    }
    const nearest = nearestPad(visible(), loc);
    geoBanner.textContent = nearest
      ? `Closest: ${nearest.pad.name} (${milesAwayLabel(nearest.meters)})`
      : "";
    geoBanner.hidden = !nearest;
  };

  const onFix = (fix: LatLng | null): void => {
    if (fix) {
      loc = fix;
      if (geoBtn) {
        geoBtn.textContent = GEO_LABEL_LOCATED;
        geoBtn.disabled = true;
      }
      // A fix that beats the catalog home just updates `loc`; `paint()` no-ops
      // until the data lands and then renders nearest-first anyway.
      paint();
      paintBanner();
    } else {
      if (geoBtn) {
        geoBtn.textContent = GEO_LABEL_IDLE;
        geoBtn.disabled = false;
      }
      if (geoBanner) {
        // A blocked permission is actionable; other failures just degrade.
        geoBanner.textContent =
          lastLocationError() === "denied" ? GEO_DENIED_COPY : "Location unavailable. Showing all pads.";
        geoBanner.hidden = false;
      }
    }
  };

  geoBtn?.addEventListener("click", () => {
    geoBtn.disabled = true;
    geoBtn.textContent = GEO_LABEL_LOCATING;
    void getUserLocation().then(onFix);
  });

  if (loc) {
    if (geoBtn) {
      geoBtn.textContent = GEO_LABEL_LOCATED;
      geoBtn.disabled = true;
    }
  } else if (hasLocationOptIn()) {
    void getUserLocation().then(onFix);
  }

  // --- Phase 2: the catalog lands ------------------------------------------
  // The pad catalog itself is static (`CITY.splashCatalog`), so this await is
  // really about `live` and — for the Open-now filter — the warmed statuses.
  const { live: isLive } = await loadPools();
  live = isLive;
  // Open-now filters on live status, so warm the cache before filtering.
  if (filters.openNow && live) await statusP;
  matching = filters.openNow
    ? SPLASH_PADS.filter((p) => poolIsOpenNow(cachedStatus(splashPoolId(p))))
    : SPLASH_PADS;
  ready = true;

  const devBanner = app.querySelector<HTMLElement>('[data-role="dev-banner"]');
  if (devBanner) devBanner.innerHTML = live ? "" : DEV_BANNER_HTML;

  // Render the list into the live DOM (sorts, wires handlers, paints statuses)
  // — this is the call that replaces the skeletons.
  paint();
  paintBanner();
}
